'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight, ChevronDown, Play, Search, X } from 'lucide-react'
import type { Video } from '@/payload-types'
import {
  VideoCardContent,
  relationName as relationLabel,
  relationSlug,
  videoCardClass,
} from '../../components/Videos/VideoCard'

type FormatFilter = 'all' | 'full' | 'short'

interface VideosListClientProps {
  videos: Video[]
  initialFormat?: FormatFilter
  initialVisaSlug?: string | null
  initialPersonaSlug?: string | null
}

function videoSearchText(video: Video): string {
  const visaNames = video.visaTypes?.map((v) => relationLabel(v)).filter(Boolean).join(' ') || ''
  const personaNames = video.personas?.map((p) => relationLabel(p)).filter(Boolean).join(' ') || ''
  return [video.title, video.description, visaNames, personaNames]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const FORMAT_TABS: { value: FormatFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'short', label: 'Clips' },
  { value: 'full', label: 'Full-length' },
]

function VideoPlayerModal({ video, onClose }: { video: Video; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  const isShort = video.format === 'short'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm md:p-8"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-5 right-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className={`w-full ${isShort ? 'max-w-100' : 'max-w-5xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`relative w-full overflow-hidden rounded-2xl bg-black shadow-2xl ${
            isShort ? 'aspect-9/16 max-h-[80vh]' : 'aspect-video'
          }`}
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        </div>
        <h2 className="mt-4 text-center text-lg text-white md:text-xl">
          <Link
            href={`/videos/${video.slug}`}
            className="inline-flex items-center gap-1.5 transition-colors hover:text-brand-gold-light"
          >
            {video.title}
            <ArrowUpRight className="h-4 w-4 shrink-0" />
          </Link>
        </h2>
      </div>
    </div>
  )
}

/**
 * Real link to the video's page (crawlable, supports modifier-clicks), but a
 * plain click plays it inline so browsing never leaves the list.
 */
function VideoCard({ video, onPlay }: { video: Video; onPlay: (video: Video) => void }) {
  return (
    <Link
      href={`/videos/${video.slug}`}
      className={videoCardClass(video.format)}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        onPlay(video)
      }}
    >
      <VideoCardContent video={video} />
    </Link>
  )
}

export function VideosListClient({
  videos,
  initialFormat = 'all',
  initialVisaSlug = null,
  initialPersonaSlug = null,
}: VideosListClientProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [formatFilter, setFormatFilter] = useState<FormatFilter>(initialFormat)
  const [selectedVisaSlug, setSelectedVisaSlug] = useState<string | null>(initialVisaSlug)
  const [selectedPersonaSlug, setSelectedPersonaSlug] = useState<string | null>(initialPersonaSlug)
  const [activeVideo, setActiveVideo] = useState<Video | null>(null)

  const closePlayer = useCallback(() => setActiveVideo(null), [])

  const visaOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const video of videos) {
      for (const vt of video.visaTypes || []) {
        const slug = relationSlug(vt)
        const name = relationLabel(vt)
        if (slug && name) map.set(slug, name)
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [videos])

  const personaOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const video of videos) {
      for (const p of video.personas || []) {
        const slug = relationSlug(p)
        const name = relationLabel(p)
        if (slug && name) map.set(slug, name)
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [videos])

  const filteredVideos = useMemo(() => {
    let result = videos

    const trimmedQuery = searchQuery.trim().toLowerCase()
    if (trimmedQuery) {
      result = result.filter((v) => videoSearchText(v).includes(trimmedQuery))
    }

    if (formatFilter !== 'all') {
      result = result.filter((v) => v.format === formatFilter)
    }

    if (selectedVisaSlug) {
      result = result.filter((v) =>
        (v.visaTypes || []).some((vt) => relationSlug(vt) === selectedVisaSlug),
      )
    }

    if (selectedPersonaSlug) {
      result = result.filter((v) =>
        (v.personas || []).some((p) => relationSlug(p) === selectedPersonaSlug),
      )
    }

    return result
  }, [videos, searchQuery, formatFilter, selectedVisaSlug, selectedPersonaSlug])

  const fullLengthVideos = useMemo(
    () => filteredVideos.filter((v) => v.format === 'full'),
    [filteredVideos],
  )
  const clipVideos = useMemo(
    () => filteredVideos.filter((v) => v.format !== 'full'),
    [filteredVideos],
  )
  const showSectionHeadings = fullLengthVideos.length > 0 && clipVideos.length > 0

  const hasActiveFilters =
    !!searchQuery || formatFilter !== 'all' || !!selectedVisaSlug || !!selectedPersonaSlug

  // The native caret crowds the pill's rounded edge, so it's swapped for a positioned chevron.
  const selectClass =
    'w-full cursor-pointer appearance-none rounded-full border border-gray-300 bg-white py-2.5 pr-11 pl-4 text-sm text-gray-700 transition-all focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 focus:outline-none'
  const caret = (
    <ChevronDown className="pointer-events-none absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 text-gray-500" />
  )

  return (
    <div>
      <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="inline-flex w-fit rounded-full border border-gray-300 bg-white p-1">
          {FORMAT_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setFormatFilter(tab.value)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors md:px-5 ${
                formatFilter === tab.value
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {visaOptions.length > 0 && (
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedVisaSlug || ''}
                onChange={(e) => setSelectedVisaSlug(e.target.value || null)}
                className={selectClass}
              >
                <option value="">All visa types</option>
                {visaOptions.map(([slug, name]) => (
                  <option key={slug} value={slug}>
                    {name}
                  </option>
                ))}
              </select>
              {caret}
            </div>
          )}

          {personaOptions.length > 0 && (
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedPersonaSlug || ''}
                onChange={(e) => setSelectedPersonaSlug(e.target.value || null)}
                className={selectClass}
              >
                <option value="">Everyone</option>
                {personaOptions.map(([slug, name]) => (
                  <option key={slug} value={slug}>
                    {name}
                  </option>
                ))}
              </select>
              {caret}
            </div>
          )}

          <div className="relative w-full sm:w-70">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary">
                <Search className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search videos"
              className="w-full rounded-full border border-gray-300 bg-white py-2.5 pr-9 pl-12 text-sm text-gray-900 placeholder:text-gray-600 focus:border-transparent focus:ring-2 focus:ring-brand-blue focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <p className="mb-6 text-sm text-gray-600">
          {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
          {searchQuery && <span> matching &ldquo;{searchQuery}&rdquo;</span>}
        </p>
      )}

      {fullLengthVideos.length > 0 && (
        <section className="mb-12">
          {showSectionHeadings && (
            <h2 className="mb-4 text-sm font-semibold tracking-widest text-gray-500 uppercase">
              Full-length
            </h2>
          )}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {fullLengthVideos.map((video) => (
              <VideoCard key={video.id} video={video} onPlay={setActiveVideo} />
            ))}
          </div>
        </section>
      )}

      {clipVideos.length > 0 && (
        <section>
          {showSectionHeadings && (
            <h2 className="mb-4 text-sm font-semibold tracking-widest text-gray-500 uppercase">
              Clips
            </h2>
          )}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {clipVideos.map((video) => (
              <VideoCard key={video.id} video={video} onPlay={setActiveVideo} />
            ))}
          </div>
        </section>
      )}

      {filteredVideos.length === 0 && (
        <div className="py-20 text-center">
          <Play className="mx-auto mb-4 h-16 w-16 text-gray-500" strokeWidth={1} />
          <h2 className="mb-2 font-serif text-2xl text-gray-500">No videos found</h2>
          <p className="mb-4 text-gray-600">
            {hasActiveFilters
              ? 'Try adjusting your search or filters.'
              : 'Check back soon for new immigration videos.'}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                setFormatFilter('all')
                setSelectedVisaSlug(null)
                setSelectedPersonaSlug(null)
              }}
              className="font-medium text-brand-gold transition-colors hover:text-brand-gold-light"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {activeVideo && <VideoPlayerModal video={activeVideo} onClose={closePlayer} />}
    </div>
  )
}
